// src/utils/constants.js

// API Configuration
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL || 'http://localhost:8080/api',
  TIMEOUT: 10000,
};

// Product Categories
export const PRODUCT_CATEGORIES = {
  T_SHIRT: 'T_SHIRT',
  HOODIE: 'HOODIE',
};

export const CATEGORY_LABELS = {
  T_SHIRT: 'T-Shirt',
  HOODIE: 'Hoodie',
};

// Product Sizes
// NOTE: the stored/enum value (e.g. 'XXL') differs from what customers see ('2XL').
// Enum names can't start with a digit, so display labels are mapped separately.
export const SIZES = ['S', 'M', 'L', 'XL', 'XXL'];

// Enum value -> customer-facing label
export const SIZE_LABELS = {
  S: 'S',
  M: 'M',
  L: 'L',
  XL: 'XL',
  XXL: '2XL',
};

// Safe getter: falls back to the raw value for any unmapped size
export const getSizeLabel = (size) => SIZE_LABELS[size] || size;

// Sort a product's availableSizes into canonical order (S, M, L, XL, 2XL),
// regardless of the order the rows were seeded/returned by the API.
// Any size not in SIZES sorts to the end, keeping its relative order.
export const sortBySizeOrder = (sizes = []) =>
  [...sizes].sort((a, b) => {
    const ia = SIZES.indexOf(a.size);
    const ib = SIZES.indexOf(b.size);
    return (ia === -1 ? SIZES.length : ia) - (ib === -1 ? SIZES.length : ib);
  });

// Order Statuses
export const ORDER_STATUS = {
  PENDING: 'PENDING',
  CONFIRMED: 'CONFIRMED',
  PROCESSING: 'PROCESSING',
  SHIPPED: 'SHIPPED',
  DELIVERED: 'DELIVERED',
  CANCELLED: 'CANCELLED',
  REFUNDED: 'REFUNDED',
};

export const ORDER_STATUS_LABELS = {
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  PROCESSING: 'Processing',
  SHIPPED: 'Shipped',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  REFUNDED: 'Refunded',
};

export const ORDER_STATUS_COLORS = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  PROCESSING: 'bg-purple-100 text-purple-800',
  SHIPPED: 'bg-indigo-100 text-indigo-800',
  DELIVERED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
  REFUNDED: 'bg-gray-100 text-gray-800',
};

// Payment Methods
export const PAYMENT_METHODS = {
  RAZORPAY: 'RAZORPAY',
  COD: 'COD',
};

// Feature Flags
export const FEATURES = {
  COD_ENABLED: false, // Set to true when ready to support Cash on Delivery
};

export const PAYMENT_METHOD_LABELS = {
  RAZORPAY: 'Online Payment (Card/UPI/Wallet)',
  COD: 'Cash on Delivery',
};

// Razorpay Configuration
export const RAZORPAY_CONFIG = {
  KEY_ID: import.meta.env.VITE_RAZORPAY_KEY_ID || 'rzp_test_your_key_id',
};

// Custom Name Validation
export const CUSTOM_NAME_CONFIG = {
  MAX_LENGTH: 50,
  PATTERN: /^[a-zA-Z\s]*$/,  // Only letters and spaces
  PLACEHOLDER: 'Enter name for printing (optional)',
  HELPER_TEXT: 'Max 50 characters. Letters only.',
};

// Shipping Configuration
export const SHIPPING_CONFIG = {
  FREE_SHIPPING_THRESHOLD: 999,  // Free shipping above ₹999
  SHIPPING_CHARGE: 50,            // ₹50 shipping charge
};

// Tax Configuration
// GST is not a single flat rate — apparel is 5% up to ₹2,500/piece and 18% above.
// The rate is computed per item on the server (see GstTaxService); the cart and
// order responses carry the resolved `tax` amount, so no rate is hardcoded here.

// Pagination
export const PAGINATION = {
  PRODUCTS_PER_PAGE: 12,
  ORDERS_PER_PAGE: 10,
};

// Local Storage Keys
export const STORAGE_KEYS = {
  TOKEN: 'token',
  USER: 'user',
  CART_ID: 'cartId',
};

// Image Placeholders
export const PLACEHOLDERS = {
  PRODUCT_IMAGE: '/images/placeholder-product.png',
  COLLEGE_LOGO: '/images/placeholder-college.png',
  USER_AVATAR: '/images/placeholder-avatar.png',
};

// Routes
export const ROUTES = {
  HOME: '/',
  PRODUCTS: '/products',
  PRODUCT_DETAIL: '/products/:id',
  CART: '/cart',
  CHECKOUT: '/checkout',
  ORDERS: '/orders',
  ORDER_DETAIL: '/orders/:id',
  LOGIN: '/login',
  SIGNUP: '/signup',
  PROFILE: '/profile',
  ADDRESSES: '/addresses',
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'Please login to continue.',
  FORBIDDEN: 'You do not have permission to access this resource.',
  NOT_FOUND: 'Resource not found.',
  SERVER_ERROR: 'Something went wrong. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Login successful!',
  SIGNUP_SUCCESS: 'Account created successfully!',
  LOGOUT_SUCCESS: 'Logged out successfully!',
  CART_ADD_SUCCESS: 'Item added to cart!',
  CART_UPDATE_SUCCESS: 'Cart updated!',
  CART_REMOVE_SUCCESS: 'Item removed from cart!',
  ORDER_SUCCESS: 'Order placed successfully!',
  ADDRESS_ADD_SUCCESS: 'Address added successfully!',
  ADDRESS_UPDATE_SUCCESS: 'Address updated!',
  ADDRESS_DELETE_SUCCESS: 'Address deleted!',
};